// PersonView.jsx — Modern Redesign
// Drop-in replacement. Retains all original logic; only visual layer changed.

/* eslint-disable react-hooks/exhaustive-deps */
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  SimpleGrid,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { CiEdit } from "react-icons/ci";
import { FaAddressCard, FaHandHoldingMedical } from "react-icons/fa";
import { FaArrowLeftLong } from "react-icons/fa6";
import { TbViewportWide } from "react-icons/tb";

import CustomModal from "../../../Components/Common/CustomModal/CustomModal";
import DeduplicationLink from "../../../Components/Eddss/DuplicateCheckLink";
import ViewInfo from "../../../Components/Eddss/personView/ViewInfo";
import { AuthContext } from "../../../Context/AuthProvider";
import LoadingSpinner from "../../../LoadingSpinner";
// import { demoPersonViewData } from "../../../utils/demoData";
import CaseInfoView from "./CaseInfoView";
import EditAddressForm from "./EditAddressForm";
import EditInformationForm from "./EditInformation";

/* ─── design tokens ─────────────────────────────────────────── */
const token = {
  surface: "#ffffff",
  surfaceAlt: "#f8f9fc",
  border: "#e4e8f0",
  btnDark: "#1a2e35",
  btnDarkHover: "#0f1e23",
  btnTeal: "#2aaa8a",
  btnTealHover: "#229070",
  btnTealSoft: "#e6f7f3",
  accent: "#2aaa8a",
  accentSoft: "#e6f7f3",
  label: "#6b7280",
  text: "#111827",
  subtext: "#374151",
  shadow: "0 1px 4px rgba(0,0,0,.06), 0 4px 16px rgba(0,0,0,.06)",
  radius: "14px",
  btnRadius: "6px",
};

/* ─── tiny helpers ───────────────────────────────────────────── */
const Card = ({ children, style = {}, ...p }) => (
  <Box
    bg={token.surface}
    border={`1px solid ${token.border}`}
    borderRadius={token.radius}
    boxShadow={token.shadow}
    p={6}
    style={style}
    {...p}
  >
    {children}
  </Box>
);

const SectionHeading = ({ children }) => (
  <Text
    fontSize="xs"
    fontWeight={700}
    letterSpacing="0.1em"
    textTransform="uppercase"
    color={token.label}
    mb={4}
  >
    {children}
  </Text>
);

const FieldRow = ({ label, value }) => (
  <Flex
    py="6px"
    borderBottom={`1px solid ${token.border}`}
    _last={{ borderBottom: "none" }}
    align="flex-start"
    gap={3}
  >
    <Text w="42%" fontSize="xs" color={token.label} fontWeight={500} pt="2px">
      {label.replace(/_/g, " ")}
    </Text>
    <Box flex={1} fontSize="sm" color={token.text} fontWeight={500}>
      {value}
    </Box>
  </Flex>
);

/* ─── formatDate (used by both PersonView and AddressSection) ── */
const formatDate = (dateStr, key) => {
  if (!dateStr) return "";
  if (/^\d{8}$/.test(String(dateStr))) {
    const parsed = moment(dateStr, "YYYYMMDD", true);
    if (parsed.isValid()) return parsed.format("MM-DD-YYYY");
  }
  if (key === "BIRTH_DATE" || key.toLowerCase().includes("date")) {
    const parsed = moment(
      dateStr,
      ["YYYY-MM-DD", "MM-DD-YYYY", moment.ISO_8601],
      true,
    );
    if (parsed.isValid()) return parsed.format("MM-DD-YYYY");
  }
  return dateStr;
};

/* ─── AddressSection (redesigned) ───────────────────────────── */
export const AddressSection = ({ addressData = [], viewAllIcon }) => {
  const { onOpen, isOpen, onClose } = useDisclosure();
  const first = addressData?.[0];

  const AddressCard = ({ addr }) => {
    const rows = Object.entries(addr || {}).filter(
      ([, v]) => v && String(v).trim(),
    );
    return (
      <Box>
        {rows.map(([k, v]) => (
          <FieldRow key={k} label={k} value={formatDate(String(v), k)} />
        ))}
      </Box>
    );
  };

  return (
    <Card h="100%">
      <Flex align="center" justify="space-between" mb={4}>
        <SectionHeading>Address Information</SectionHeading>
        {addressData.length > 1 && (
          <Button
            size="sm"
            bg="gray.800"
            color="white"
            borderRadius="sm"
            fontWeight="500"
            px={3}
            leftIcon={<FaAddressCard size="12px" />}
            _hover={{ bg: "gray.700" }}
            _active={{ bg: "gray.900" }}
            onClick={onOpen}
          >
            View All ({addressData.length})
          </Button>
        )}
      </Flex>

      {first ? (
        <AddressCard addr={first} />
      ) : (
        <Text fontSize="sm" color={token.label}>
          No address on record.
        </Text>
      )}

      {/* View-all modal */}
      <CustomModal
        size="6xl"
        isCentered
        title="All Addresses"
        isOpen={isOpen}
        onClose={onClose}
        bodyContent={
          <SimpleGrid columns={2} spacing={6} p={2} minH="50vh">
            {addressData.map((addr, i) => (
              <Card key={i}>
                <Text
                  fontSize="xs"
                  fontWeight={700}
                  color={token.label}
                  mb={3}
                  textTransform="uppercase"
                  letterSpacing=".08em"
                >
                  Address {i + 1}
                </Text>
                <AddressCard addr={addr} />
              </Card>
            ))}
          </SimpleGrid>
        }
      />
    </Card>
  );
};

/* ─── PersonView (redesigned) ───────────────────────────────── */
const PersonView = () => {
  const [personData, setPersonData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [information, setInformation] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editAddressData, setEditAddressData] = useState([]);
  const [showSSN, setShowSSN] = useState(false);

  const { personID } = useParams();
  const navigate = useNavigate();
  const { onOpen, isOpen, onClose } = useDisclosure();
  const { isAuthenticated, isAdmin } = useContext(AuthContext);

  const fieldsToDisplay = [
    "FIRST_NAME",
    "LAST_NAME",
    "MIDDLE_NAME",
    "MAIDEN_NAME",
    "ALIAS",
    "PREFIX",
    "BIRTH_DATE",
    "GENDER",
    "RACE",
    "TRIBE",
    "SSN",
    "TWINS",
    "DEDUPLICATION",
    "MODIFIED_BY",
  ];

  const arrayFields = Object.entries(personData)
    .filter(([, v]) => Array.isArray(v) && v.length > 0)
    .map(([key]) => ({
      key,
      label: `View ${key.replace(/_/g, " ")}`,
      icon:
        key === "ADDRESS" ? (
          <FaAddressCard fontSize="18px" />
        ) : key === "CASES" ? (
          <FaHandHoldingMedical fontSize="18px" />
        ) : (
          <TbViewportWide fontSize="18px" />
        ),
    }));

  const caseFields = arrayFields.filter(({ key }) => key === "CASES");
  const historyFields = arrayFields.filter(({ key }) => key === "HISTORY");

  const fetchPersonData = () => {
    if (!personID) return;
    setIsLoading(true);
    fetch(`/api/getPerson?id=${personID}`)
      .then((r) => r.json())
      .then(setPersonData)
      .catch(() => toast.error("Failed to fetch person data."))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchPersonData();
  }, [personID]);

  const handleViewInfo = (key, data) => {
    setInformation({ key, data });
    onOpen();
  };

  const handleCombinedSave = () => {
    const payload = buildUpdatePayload(
      personID,
      personData,
      editFormData,
      editAddressData,
    );
    if (!payload) {
      toast.info("No changes detected.");
      return;
    }
    sendUpdate(payload);
  };

  const sendUpdate = (payload) => {
    setIsLoading(true);
    fetch("/api/savePerson", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.text();
      })
      .then((t) => {
        if (t.toLowerCase().includes("ok")) {
          toast.success("Person updated successfully.");
          fetchPersonData();
        } else throw new Error();
      })
      .catch(() => toast.error("Failed to update. Please try again."))
      .finally(() => {
        setEditFormData({});
        setEditAddressData([]);
        setIsEditOpen(false);
        setIsLoading(false);
      });
  };

  if (isLoading) return <LoadingSpinner />;

  /* ── build display rows ── */
  const personalRows = Object.entries(personData).filter(([key]) =>
    fieldsToDisplay.includes(key),
  );

  return (
    <Box
      minH="100vh"
      bg={token.surfaceAlt}
      fontFamily="'DM Sans', sans-serif"
      px={{ base: 4, md: 8 }}
      py={6}
    >
      {/* ── Page header ── */}
      <Flex align="center" gap={4} mb={6}>
        <IconButton
          aria-label="Back"
          icon={<FaArrowLeftLong />}
          variant="ghost"
          size="sm"
          borderRadius="10px"
          border={`1px solid ${token.border}`}
          bg={token.surface}
          onClick={() => navigate(-1)}
          _hover={{ bg: token.accentSoft }}
        />
        <Box>
          <Text
            fontSize="xl"
            fontWeight={700}
            color={token.text}
            lineHeight={1}
          >
            Person Profile
          </Text>
          <Text fontSize="xs" color={token.label} mt="2px">
            ID: {personID}
          </Text>
        </Box>

        <HStack ml="auto" spacing={2}>
          {isAuthenticated && isAdmin && (
            <DeduplicationLink
              personID={personID}
              DEDUPLICATION={personData?.DEDUPLICATION}
              isAdmin={isAdmin}
            />
          )}
          <Button
            leftIcon={<CiEdit fontSize="15px" />}
            size="sm"
            bg="teal.500"
            color="white"
            borderRadius="sm"
            fontWeight="500"
            px={5}
            _hover={{ bg: "teal.400" }}
            _active={{ bg: "teal.600" }}
            onClick={() => {
              setEditFormData(personData);
              setEditAddressData(personData?.ADDRESS || []);
              setIsEditOpen(true);
            }}
          >
            Edit Profile
          </Button>
        </HStack>
      </Flex>

      {/* ── Main 2-col grid ── */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mb={5}>
        {/* Personal info card */}
        <Card>
          <SectionHeading>Personal Information</SectionHeading>

          {personalRows.map(([key, value]) => {
            let display;
            if (key === "SSN") {
              if (!value) {
                display = <Text color={token.label}>—</Text>;
              } else {
                display = (
                  <HStack spacing={2}>
                    <Text fontFamily="monospace">
                      {showSSN ? value : "•••  ••  ••••"}
                    </Text>
                    <IconButton
                      size="xs"
                      border="none"
                      aria-label={showSSN ? "Hide SSN" : "Show SSN"}
                      icon={showSSN ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowSSN((p) => !p)}
                    />
                  </HStack>
                );
              }
            } else if (Array.isArray(value)) {
              display = (
                <HStack wrap="wrap" spacing={2}>
                  {value.map((item, idx) => (
                    <Badge
                      key={idx}
                      bg={token.accentSoft}
                      color={token.accent}
                      borderRadius="6px"
                      px={2}
                      py="2px"
                      fontSize="xs"
                      fontWeight={600}
                      textTransform="none"
                    >
                      {item}
                    </Badge>
                  ))}
                </HStack>
              );
            } else {
              display = (
                <Text color={value ? token.text : token.label}>
                  {formatDate(value, key) || "—"}
                </Text>
              );
            }

            return <FieldRow key={key} label={key} value={display} />;
          })}
        </Card>

        {/* Address card */}
        <AddressSection
          addressData={personData["ADDRESS"]}
          viewAllIcon={<FaAddressCard fontSize="20px" />}
        />
      </SimpleGrid>

      {/* ── History ── */}
      {historyFields.length > 0 && (
        <Card mb={5}>
          <SectionHeading>History</SectionHeading>
          <HStack spacing={3} wrap="wrap">
            {historyFields.map(({ key, label, icon }) => (
              <Button
                key={key}
                leftIcon={icon}
                size="sm"
                bg="gray.800"
                color="white"
                borderRadius="sm"
                fontWeight="500"
                px={5}
                _hover={{ bg: "gray.700" }}
                _active={{ bg: "gray.900" }}
                onClick={() => handleViewInfo(key, personData[key])}
              >
                {label}
              </Button>
            ))}
          </HStack>
        </Card>
      )}

      {/* ── Cases ── */}
      {caseFields.length > 0 && (
        <Card>
          <SectionHeading>Case Information</SectionHeading>
          {caseFields.map(({ key }) => (
            <CaseInfoView
              key={key}
              info={{ key, data: personData[key] }}
              data={personData}
            />
          ))}
        </Card>
      )}

      {/* ── Edit modal ── */}
      <CustomModal
        size="6xl"
        title="Edit Person"
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        bodyContent={
          <Flex gap={4} minH="70vh" maxH="75vh">
            <Box w="40%">
              <EditInformationForm
                initialData={editFormData}
                onChange={setEditFormData}
                hideButtons
              />
            </Box>
            <Divider orientation="vertical" />
            <Box w="60%">
              <EditAddressForm
                initialData={editAddressData}
                onChange={setEditAddressData}
              />
            </Box>
          </Flex>
        }
        footerContent={
          <HStack spacing={3}>
            <Button
              size="sm"
              bg="gray.800"
              color="white"
              borderRadius="sm"
              fontWeight="500"
              px={5}
              _hover={{ bg: "gray.700" }}
              _active={{ bg: "gray.900" }}
              onClick={() => setIsEditOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              bg="teal.500"
              color="white"
              borderRadius="sm"
              fontWeight="500"
              px={5}
              _hover={{ bg: "teal.400" }}
              _active={{ bg: "teal.600" }}
              onClick={handleCombinedSave}
            >
              Save Changes
            </Button>
          </HStack>
        }
      />

      {/* ── View info modal ── */}
      <CustomModal
        size="6xl"
        isCentered
        title={`View ${information?.key}`}
        isOpen={isOpen}
        onClose={onClose}
        bodyContent={<ViewInfo info={information} />}
      />
    </Box>
  );
};

export default PersonView;

/* ─── pure helpers (unchanged logic) ────────────────────────── */
const isAddressChanged = (original, updated) => {
  if (!original || original.length !== updated.length) return true;
  for (let i = 0; i < updated.length; i++) {
    const o = original[i] || {},
      u = updated[i] || {};
    for (const key of new Set([...Object.keys(o), ...Object.keys(u)])) {
      if ((o[key] || "") !== (u[key] || "")) return true;
    }
  }
  return false;
};

const buildUpdatePayload = (
  personID,
  originalData,
  updatedPersonData,
  updatedAddressData,
) => {
  const changes = {};
  for (const key in updatedPersonData) {
    if (updatedPersonData[key] !== originalData[key]) {
      const value = updatedPersonData[key];
      changes[key] =
        typeof value === "string" && moment(value, "YYYY-MM-DD", true).isValid()
          ? moment(value).format("YYYYMMDD")
          : value;
    }
  }

  if (isAddressChanged(originalData.ADDRESS, updatedAddressData)) {
    changes.ADDRESS = updatedAddressData.map((addr) => {
      const formatted = {};
      for (const key in addr) {
        if (key === "TELEPHONE_WORK_EXT") continue;
        let val = addr[key];
        if (key === "TELEPHONE_WORK" && addr["TELEPHONE_WORK_EXT"])
          val += ` x${addr["TELEPHONE_WORK_EXT"]}`;
        formatted[key] =
          typeof val === "string" &&
          key.toLowerCase().includes("date") &&
          moment(val, "YYYY-MM-DD", true).isValid()
            ? moment(val).format("YYYYMMDD")
            : val;
      }
      return formatted;
    });
  }

  if (Object.keys(changes).length === 0) return null;
  return { id: personID, ...changes };
};
